import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { take, tap } from 'rxjs/operators';
import { BillingError } from 'src/app/pages/new-billing/models/billing-error';
import { ConcretePlan } from 'src/app/pages/new-billing/models/plan.model';
import { InvoicePreviewData, InvoicePreviewItem } from 'src/app/pages/new-billing/types/api.types';
import { BillingService } from 'src/app/services/billing/billing.service';
import { LocalizedPricingService } from 'src/app/services/localized-pricing/localized-pricing.service';
import { convertCentsToDollars, normalizePrice } from 'src/app/util/price-formatter.util';


/**
 * Configuration for the upcoming invoice preview dialog
 * @property currentPlan - The current plan details
 * @property isV2 - Whether to use the V2 styling
 * @property skeletonConfig - Optional configuration for the skeleton loader in V2 mode
 * @property skeletonConfig.itemHeight - Custom height for skeleton items (e.g., '40px')
 * @property skeletonConfig.itemWidth - Custom width for skeleton items (e.g., '90%')
 * @property skeletonConfig.itemMargin - Custom margin for skeleton items (e.g., '12px 0')
 */
export type InvoicePreviewDialogData = {
  currentPlan: ConcretePlan;
  isV2?: boolean;
  skeletonConfig?: {
    itemHeight?: string;
    itemWidth?: string;
    itemMargin?: string;
  };
};


@Component({
  selector: 'app-upcoming-invoice-preview',
  templateUrl: './upcoming-invoice-preview.component.html',
  styleUrls: ['./upcoming-invoice-preview.component.scss'],
})
export class UpcomingInvoicePreviewComponent implements OnInit, OnDestroy {
  skeletonItems = Array(7);
  status: 'loading' | 'idle' | 'error' = 'loading';
  errorTitle: string;
  errorDesc: string;

  displayedColumns: string[] = ['product', 'quantity', 'unitPrice', 'amount'];
  dataSource = new MatTableDataSource([] as InvoicePreviewItem[]);
  upcomingInvoiceData: InvoicePreviewData;

  private skeletonTimeoutId: number | null = null;

  get disclaimerRecurrence() {
    return this.data.currentPlan.paymentModel.paymentRecurrence === 'month'
      ? 'billing.recurrenceMonthly'
      : 'billing.recurrenceAnnual';
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: InvoicePreviewDialogData,
    private readonly billingService: BillingService,
    private readonly localizedPricingService: LocalizedPricingService,
    public dialogRef: MatDialogRef<UpcomingInvoicePreviewComponent>,
  ) {}

  ngOnInit(): void {
    this.initTable();
    if (this.data.isV2 && this.data.skeletonConfig) {
      this.applySkeletonConfig();
    }
  }

  /**
   * Applies custom skeleton configuration for V2 mode
   * This method sets CSS custom properties on the skeleton loader element
   * based on the skeletonConfig provided in the dialog data.
   *
   * To use this feature:
   * 1. Pass a skeletonConfig object when opening the dialog
   * 2. The skeleton loader will use these values instead of the defaults
   *
   * @private
   */
  private applySkeletonConfig() {
    const { skeletonConfig } = this.data;
    if (!skeletonConfig) return;

    // Wait for the DOM to be ready
    this.skeletonTimeoutId = window.setTimeout(() => {
      const skeletonLoader = document.querySelector('.skeleton-loader.v2');
      if (skeletonLoader instanceof HTMLElement) {
        if (skeletonConfig.itemHeight) {
          skeletonLoader.style.setProperty('--skeleton-item-height', skeletonConfig.itemHeight);
        }
        if (skeletonConfig.itemWidth) {
          skeletonLoader.style.setProperty('--skeleton-item-width', skeletonConfig.itemWidth);
        }
        if (skeletonConfig.itemMargin) {
          skeletonLoader.style.setProperty('--skeleton-item-margin', skeletonConfig.itemMargin);
        }
      }
    });
  }

  private initTable(): void {
    this.billingService.getUpcomingInvoicePreview().pipe(
      take(1),
      tap((invoicePreviewData) => this.upcomingInvoiceData = invoicePreviewData),
    ).subscribe({
      next: (invoicePreviewData) => {
        this.dataSource.data = invoicePreviewData.items;
        this.status = 'idle';
      },
      error: (error: unknown) => {
        this.status = 'error';
        this.errorTitle = 'billing.invoice.errors.default.title';
        this.errorDesc = 'billing.invoice.errors.default.description';

        if (error instanceof BillingError && error.errorCode === 'unsupported_subscription_status') {
          this.errorTitle = 'billing.invoice.errors.subscriptionLocked.title';
          this.errorDesc = 'billing.invoice.errors.subscriptionLocked.description';
        }
      },
    });
  }

  ngOnDestroy(): void {
    if (this.skeletonTimeoutId !== null) {
      clearTimeout(this.skeletonTimeoutId);
      this.skeletonTimeoutId = null;
    }
  }

  /**
   * Formats a price with localized currency for template usage
   * @param price - The price in cents (will be converted to dollars)
   * @returns string - Formatted price with appropriate currency symbol
   */
  formatLocalizedPrice(price: number): string {
    const priceInDollars = convertCentsToDollars(price);
    const billingCurrency = this.data.currentPlan?.billingApiData?.billingDetails?.currency;

    // Priority 1: Always respect billing currency if available
    if (billingCurrency && this.data.currentPlan) {
      try {
        return this.localizedPricingService.formatPriceWithCurrency(priceInDollars, billingCurrency, 'excludePlanAndAddon');
      } catch (error) {
        // Error formatting price with billing currency
      }
    }

    // Priority 2: If no card is added, try to get price from price overrides based on user's country
    const noCardPrice = this.localizedPricingService.getLocalizedPriceForNoCard(this.data.currentPlan, priceInDollars);
    if (noCardPrice) {
      return this.localizedPricingService.formatPriceWithCurrency(noCardPrice.price, noCardPrice.currency, 'excludePlanAndAddon');
    }

    // Priority 3: Use localized pricing service if enabled and no billing currency
    const isEnabled = this.localizedPricingService.isLocalizedPricingEnabled();
    if (isEnabled && !billingCurrency && this.data.currentPlan) {
      try {
        return this.localizedPricingService.formatLocalizedPriceForComponent(priceInDollars, this.data.currentPlan, true, 'excludePlanAndAddon');
      } catch (error) {
        // Error getting localized price
      }
    }

    // Priority 4: Fallback to USD formatting
    const normalizedPrice = normalizePrice(priceInDollars);

    // Use proper locale-based formatting for USD
    const formattedNumber = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Math.abs(normalizedPrice));

    // Handle negative values properly
    if (normalizedPrice < 0) {
      return `-$${formattedNumber}`;
    }

    return `$${formattedNumber}`;
  }

  /**
   * Formats a discount amount with localized currency
   * @param discount - The discount amount in cents
   * @returns string - Formatted discount with appropriate currency symbol
   */
  formatLocalizedDiscount(discount: number): string {
    const formattedPrice = this.formatLocalizedPrice(discount);
    return discount > 0 ? `-${formattedPrice}` : formattedPrice;
  }

}
